import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/contexts/AuthContext";

interface AuthDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  defaultTab?: "signin" | "signup";
  prefillEmail?: string;
  showEmailHint?: boolean;
}

export default function AuthDialog({
  open,
  onOpenChange,
  onSuccess,
  defaultTab = "signin",
  prefillEmail = "",
  showEmailHint = false,
}: AuthDialogProps) {
  const { signIn, signUp } = useAuth();
  const [authTab, setAuthTab] = useState<"signin" | "signup">(defaultTab);
  const [authLoading, setAuthLoading] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const [signInEmail, setSignInEmail] = useState(prefillEmail);
  const [signInPassword, setSignInPassword] = useState("");
  const [signUpName, setSignUpName] = useState("");
  const [signUpEmail, setSignUpEmail] = useState(prefillEmail);
  const [signUpPassword, setSignUpPassword] = useState("");

  useEffect(() => {
    setAuthTab(defaultTab);
  }, [defaultTab]);

  useEffect(() => {
    if (prefillEmail) {
      setSignInEmail(prefillEmail);
      setSignUpEmail(prefillEmail);
    }
  }, [prefillEmail]);

  const handleSignInSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthError(null);
    setAuthLoading(true);
    const { error } = await signIn(signInEmail, signInPassword);
    setAuthLoading(false);
    if (error) {
      setAuthError(error.message || "Errore durante l'accesso");
      return;
    }
    onSuccess();
  };

  const handleSignUpSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthError(null);
    setAuthLoading(true);
    const { error } = await signUp(signUpEmail, signUpPassword, signUpName);
    setAuthLoading(false);
    if (error) {
      setAuthError(error.message || "Errore durante la registrazione");
      return;
    }
    onSuccess();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-950/98 backdrop-blur-xl border border-white/10 text-slate-100 sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-slate-100">Salva il tuo piano e inizia</DialogTitle>
          <DialogDescription className="text-slate-400">
            Crea un account per collegare il tuo Reality Check allo Sprint
          </DialogDescription>
        </DialogHeader>

        <Tabs
          value={authTab}
          onValueChange={(v) => {
            setAuthTab(v as "signin" | "signup");
            setAuthError(null);
          }}
          className="mt-2"
        >
          <TabsList className="grid w-full grid-cols-2 bg-white/5 border border-white/10">
            <TabsTrigger value="signin" className="data-[state=active]:bg-white/10 data-[state=active]:text-white text-slate-400">Accedi</TabsTrigger>
            <TabsTrigger value="signup" className="data-[state=active]:bg-white/10 data-[state=active]:text-white text-slate-400">Registrati</TabsTrigger>
          </TabsList>

          <TabsContent value="signin" className="mt-4">
            <form onSubmit={handleSignInSubmit} className="space-y-3">
              <input
                type="email"
                required
                placeholder="Email"
                value={signInEmail}
                onChange={(e) => setSignInEmail(e.target.value)}
                className="w-full rounded-lg bg-slate-800 border border-slate-700 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-secondary"
              />
              {showEmailHint && (
                <p className="text-xs text-slate-500 -mt-1">
                  Stessa email inserita nel Reality Check
                </p>
              )}
              <input
                type="password"
                required
                placeholder="Password"
                value={signInPassword}
                onChange={(e) => setSignInPassword(e.target.value)}
                className="w-full rounded-lg bg-slate-800 border border-slate-700 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-secondary"
              />
              {authError && <p className="text-sm text-red-400">{authError}</p>}
              <button
                type="submit"
                disabled={authLoading}
                className="w-full rounded-lg bg-gradient-to-r from-[#FF6B47] to-[#FF8A6B] text-primary-foreground font-semibold py-2 text-sm disabled:opacity-50"
              >
                {authLoading ? "Accesso..." : "Accedi"}
              </button>
            </form>
          </TabsContent>

          <TabsContent value="signup" className="mt-4">
            <form onSubmit={handleSignUpSubmit} className="space-y-3">
              <input
                type="text"
                required
                placeholder="Nome"
                value={signUpName}
                onChange={(e) => setSignUpName(e.target.value)}
                className="w-full rounded-lg bg-slate-800 border border-slate-700 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-secondary"
              />
              <input
                type="email"
                required
                placeholder="Email"
                value={signUpEmail}
                onChange={(e) => setSignUpEmail(e.target.value)}
                className="w-full rounded-lg bg-slate-800 border border-slate-700 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-secondary"
              />
              {showEmailHint && (
                <p className="text-xs text-slate-500 -mt-1">
                  Stessa email inserita nel Reality Check
                </p>
              )}
              <input
                type="password"
                required
                minLength={6}
                placeholder="Password (min. 6 caratteri)"
                value={signUpPassword}
                onChange={(e) => setSignUpPassword(e.target.value)}
                className="w-full rounded-lg bg-slate-800 border border-slate-700 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-secondary"
              />
              {authError && <p className="text-sm text-red-400">{authError}</p>}
              <button
                type="submit"
                disabled={authLoading}
                className="w-full rounded-lg bg-gradient-to-r from-[#FF6B47] to-[#FF8A6B] text-primary-foreground font-semibold py-2 text-sm disabled:opacity-50"
              >
                {authLoading ? "Creazione..." : "Crea account"}
              </button>
            </form>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
